import { useCallback, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useNavigate } from "react-router";
import { processError } from "../../../../common/utils/base_error";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { EntregaObrasListaModel } from "../../domain/models/entrega_obras_lista";
import { useEntregaObrasListaFilters } from "../hooks/entrega_obra_lista/filters";
import { useEntregaObrasListaPagination } from "../hooks/entrega_obra_lista/pagination";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { EntregaObraModel } from "../../domain/models/entrega_obra_model";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { EntregaObraCadastroForm } from "../schemas/entrega_obra_lista/entregaObraCadastro";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { DropDown } from "../../../../common/domain/models/dropdown";


type LoadEntregaObrasListaState = {
    status?: number,
    idsCentroCusto?: number[],

    currentPage?: number;
    rowsPerPage?: number;

}

export const useEntregaObrasListaViewModel = () => {

    const {
        entregaObras: {
            getAllEntregaObraUseCase,
            createEntregaObraUseCase,
            updateEntregaObraUseCase,
            deleteEntregaObraUseCase,
            getEntregaObraByIdUseCase,


        },
        commons: {
            getCentroCustoDropDownUseCase,
            getAllLogAlteracaoUseCase,
            getAllLogAlteracaoByParentUseCase,
        }
    } = useDependencyInjection();


    const navigate = useNavigate();
    const { pushErrorNotification, pushWarnNotification, pushInfoNotification, setPageLoading } = usePageLoader();
    const debouncedRef = useCreateDebounceHandler(1500);

    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [openCadastroModal, setOpenCadastroModal] = useState(false);

    const [selectedIdEntregaObra, setSelectedIdEntregaObra] = useState<number>();

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [errorMessage, setErrorMessage] = useState<string>();
    const [entregaObrasListaModel, setEntregaObrasListaModel] = useState<EntregaObrasListaModel[]>();
    const [selectedEntregaObra, setSelectedEntregaObra] = useState<EntregaObraModel | undefined>();
    const [ddlCentroCusto, setDdlCentroCusto] = useState<DropDown[]>();

    const filters = useEntregaObrasListaFilters();
    const pagination = useEntregaObrasListaPagination();


    const changeHistory = useChangeHistory({
        getAllLogAlteracaoUseCase,
        getAllLogAlteracaoByParentUseCase,
        onSetError: (e) => pushErrorNotification('Erro', [e]),
    })



    //#region CARREGAMENTO.

    const loadEntregaObrasLista = useCallback(async (funcState?: LoadEntregaObrasListaState) => {
        try {
            setLoading(true);
            const filteredStatus = evaluateParamValue(funcState?.status, filters.status);
            const filteredIdsCentroCusto = evaluateParamValue(funcState?.idsCentroCusto, filters.idsCentroCusto);

            const filteredPage = funcState?.currentPage ?? pagination.page;
            const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

            await getAllEntregaObraUseCase.execute(
                filteredIdsCentroCusto ?? [],
                filteredStatus ?? 2,

                filteredPage,
                filteredRowsPerPage,
            );

            setEntregaObrasListaModel(getAllEntregaObraUseCase.items);

            pagination.setRowsPerPage(getAllEntregaObraUseCase.pageSize);
            pagination.setTotalItems(getAllEntregaObraUseCase.totalItems);
        } catch (e: unknown) {
            setEntregaObrasListaModel([]);
            pushErrorNotification("Error", processError(e, "Falha ao buscar lista de entrega de obras!"));
        } finally {
            setLoading(false);
        }
    }, [
        filters,
        pagination,
        getAllEntregaObraUseCase,
        pushErrorNotification
    ]);


    const loadEntregaObraById = useCallback(async (idEntregaObra: number) => {
        try {
            setLoading(true);
            const entregaObra = await getEntregaObraByIdUseCase.execute(idEntregaObra);
            setSelectedEntregaObra(entregaObra);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar entrega de obra!'));
        } finally {
            setLoading(false);
        }
    }, [
        getEntregaObraByIdUseCase,
        pushErrorNotification]);


    const loadCentroCustoDropDown = useCallback(async () => {
        try {
            setLoading(true);
            const ddlCentroCusto = await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
            setDdlCentroCusto(ddlCentroCusto);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar centro de custo!'));
        } finally {
            setLoading(false);
        }
    }, [
        getCentroCustoDropDownUseCase,
        pushErrorNotification]);



    //#endregion






    //#region PAGE START

    usePageStartBootstrap(async () => {
        try {
            await loadCentroCustoDropDown();
            if (entregaObrasListaModel === undefined) {
                await loadEntregaObrasLista();
            }
        } catch (e: unknown) {
            setErrorMessage(processError(e, "Falha ao buscar lista de entrega de obras!")[0]);
        }
    });


    //#endregion


    //#region NAVEGAÇÃO

    const handleNewEntregaObra = useCallback((openNewTab: boolean) => {
        if (openNewTab) {
            window.open(`/EntregaObras_lista`, "_blank", "noopener,noreferrer");
        } else {
            navigate("/EntregaObras_lista");
        }
    },
        [navigate],
    );


    const handleEditarEntregaObrasCliente = useCallback(async (idEntregaObra: number | null, openNewTab: boolean) => {
        try {
            if (!openNewTab) {
                window.open(`/EntregaObrasCliente_lista?id=${idEntregaObra}`, "_blank", "noopener,noreferrer");
                return;
            } else {
                navigate(`/EntregaObrasCliente_lista?id=${idEntregaObra}`);
            }
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao redirecionar página!'));
        } finally {
            setLoading(false);
        }
    }, [navigate, pushErrorNotification]);


    //#endregion


    //#region NOVO/EDITAR.

    const handleEntregaObraCadastroOpenState = useCallback(async (idEntregaObra: number | null, openState: boolean) => {
        setOpenCadastroModal(openState);
        try {
            if (openState) {
                if (idEntregaObra) {
                    await loadEntregaObraById(idEntregaObra);
                } else {
                    setSelectedEntregaObra(undefined);
                }
            } else {
                setSelectedEntregaObra(undefined);
            }

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao iniciar criação de nova entrega de obra!'));
        }
    }, [
        loadEntregaObraById,
        pushErrorNotification
    ]);



    const handleEntregaObraCadastro = useCallback(async (form: EntregaObraCadastroForm) => {
        try {
            setSaving(true);

            if (form.idEntregaObra) {
                await updateEntregaObraUseCase.execute({
                    idEntregaObra: form.idEntregaObra,
                    idCentroCusto: form.idCentroCusto,
                    status: form.status,
                });
                pushInfoNotification("Sucesso", " Entrega de obra atualizada com sucesso!", { isAutoFinish: true });

            } else {
                await createEntregaObraUseCase.execute(
                    form.status,
                    form.idCentroCusto,
                );
                pushInfoNotification("Sucesso", " Entrega de obra criada com sucesso!", { isAutoFinish: true });
            }

            setOpenCadastroModal(false);
            setSelectedEntregaObra(undefined);
            await loadEntregaObrasLista();
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao salvar entrega de obra!"));
        } finally {
            setSaving(false);
        }
    }, [
        createEntregaObraUseCase,
        updateEntregaObraUseCase,
        selectedEntregaObra,
        loadEntregaObrasLista,
        pushErrorNotification,
        pushInfoNotification]);

    //#endregion





    //#region PAGINAÇÃO.

    const handleReload = useCallback(async () => {
        try {
            setLoading(true);
            await loadEntregaObrasLista();
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de entrega de obras!'));
        } finally {
            setLoading(false);
        }
    }, [loadEntregaObrasLista]);

    const handlePageChange = useCallback(async (page: number) => {
        try {
            setLoading(true);
            pagination.setPage(page + 1);
            await loadEntregaObrasLista({ currentPage: page + 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lis!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadEntregaObrasLista]);

    const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
        try {
            setLoading(true);
            pagination.setPage(1);
            pagination.setRowsPerPage(rowsPerPage);
            await loadEntregaObrasLista({ rowsPerPage, currentPage: 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de termos!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadEntregaObrasLista, pushErrorNotification]);

    //#endregion


    //#region DELECAO 

    const handleDeleteOpenState = useCallback((idEntregaObra: number | null, openState: boolean) => {
        if (openState) {
            if (idEntregaObra) {
                setSelectedIdEntregaObra(idEntregaObra);
                setOpenDeleteModal(true);
            } else {
                setSelectedIdEntregaObra(undefined);
                setOpenDeleteModal(false);
            }
        }
        setOpenDeleteModal(openState);
    }, []);


    const handleConfirmarDeletarEntregaObra = useCallback(async () => {
        try {

            setSaving(true);
            if (selectedIdEntregaObra) {
                await deleteEntregaObraUseCase.execute(selectedIdEntregaObra);
                pushInfoNotification("Sucesso", " Entrega de obra deletada com sucesso!", { isAutoFinish: true });
            } else {
                pushErrorNotification("Erro", "Não foi possivel deletar Entrega de obra!", { isAutoFinish: true });
            }
            await loadEntregaObrasLista()
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao Deletar Entrega de obra!"));
        } finally {
            setSaving(false);
            setOpenDeleteModal(false);
            setSelectedIdEntregaObra(undefined)
        }
    }, [
        selectedIdEntregaObra,
        deleteEntregaObraUseCase,
        loadEntregaObrasLista,
        pushErrorNotification,
        pushInfoNotification]);

    //#endregion





    //#region PESQUISA.

    /**
     * 
     */



    const handleIdsCentroCustoChange = useDebouncedCallback(
        debouncedRef,
        (newValue: number[] | null) => {

            filters.setIdsCentroCusto(newValue ?? []);
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadEntregaObrasLista({ idsCentroCusto: newValue ?? [], currentPage: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar termos!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadEntregaObrasLista, pushErrorNotification]
    );

    const handleStatusChange = useDebouncedCallback(
        debouncedRef,
        (newValue: number | null) => {

            filters.setStatus(newValue ?? 2);
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadEntregaObrasLista({ status: newValue ?? 2, currentPage: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar termos!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [filters, pagination, loadEntregaObrasLista, pushErrorNotification]);


    const handleClearFilters = useCallback(async () => {
        try {
            setLoading(true);
            filters.clearFilters();
            pagination.setPage(1);
            await loadEntregaObrasLista({
                idsCentroCusto: [],
                status: 2,
                currentPage: 1
            });
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao buscar entregas de obras!'));
        } finally {
            setLoading(false);
        }
    }, [
        filters,
        pagination,
        loadEntregaObrasLista,
        pushErrorNotification]);


    //#endregion 



    //#region History

    const handleOpenChangeHistory = useCallback((
        idTabela: number | null,
        tipoTabela: LogTipoTabela | null,
    ) => {
        if (idTabela && tipoTabela) {
            changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
        } else {
            if (true) {
                changeHistory.handleOpenChangeHistory(selectedEntregaObra?.idEntregaObra ?? 0, LogTipoTabela.ENTREGA_OBRA, true);
            }
        }
    }, [
        selectedEntregaObra,
        changeHistory,
    ]);

    //#endregion



    return {
        loading,
        saving,
        openDeleteModal,
        openCadastroModal,
        entregaObrasListaModel: entregaObrasListaModel ?? [],
        filters,
        pagination,
        selectedEntregaObra,
        errorMessage,
        changeHistory,
        ddlCentroCusto,

        handleEntregaObraCadastro,
        handleEditarEntregaObrasCliente,
        handleIdsCentroCustoChange,
        handleNewEntregaObra,
        handleEntregaObraCadastroOpenState,
        handleConfirmarDeletarEntregaObra,
        handleDeleteOpenState,
        handleOpenChangeHistory,
        handleStatusChange,
        handleReload,
        handlePageChange,
        handleRowsPerPageChange,
        handleClearFilters,

    };
}


